<template>
  <div class="table-wrapper block-el">
    <div class="row">
      <div class="col-md-6">
        <slot name="buttons" />
      </div>
      <div v-if="searchable" class="col-md-6">
        <form @submit.prevent="search">
          <div class="form-group">
            <div class="input-group mb-3">
              <input
                type="text"
                class="form-control"
                placeholder="Search..."
                v-model="q"
              />
              <div class="input-group-append">
                <button class="btn btn-primary btn-icon" type="submit">
                  <i class="fas fa-search"></i>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table table-borderless flex-column d-flex w-100">
            <thead>
              <tr class="d-flex justify-content-around">
                <th v-for="(column, n) in filteredColumns" :key="n">
                  {{ $tc(`app.${getColumnLabel(column)}`, 1) }}
                </th>
                <th>
                  <slot name="table-item"></slot>
                </th>
              </tr>
            </thead>
            <tbody v-show="clickable" v-for="(item, n) in data" :key="n">
              <tr class="d-flex justify-content-around">
                <td :class="clickable ? 'clickable' : ''" @click="$emit('row-click', item)" v-for="(column, i) in filteredColumns" :key="i" :data-label="$tc(`app.${getColumnLabel(column)}`, 1)">
                  <span
                    v-html="getColumnValue(item, column)"
                    :class="getColumnClass(item, column)"
                  >
                  </span>
                </td>
                <td :data-label="$tc('app.actions', 1)">
                  <slot name="actions" v-bind="item"></slot>
                </td>
              </tr>
              <tr v-if="!data.length">
                <td class="text-center" :colspan="columns.length">
                  {{ $tc('app.empty_table') }}
                </td>
              </tr>
            </tbody>
            <tbody v-if="!clickable">
              <tr v-for="(item, n) in data" :key="n">
                <td v-for="(column, i) in filteredColumns" :key="i" :data-label="$tc(`app.${getColumnLabel(column)}`, 1)">
                  <span
                    v-html="getColumnValue(item, column)"
                    :class="getColumnClass(item, column)"
                  >
                  </span>
                </td>
                <td :data-label="$tc('app.actions', 1)">
                  <slot name="actions" v-bind="item"></slot>
                </td>
              </tr>
              <tr v-if="!data.length">
                <td class="text-center" :colspan="columns.length">
                  {{ $tc('app.empty_table') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <pagination :lastPage="pages" @change="changePage" />
      </div>
    </div>
    <div v-if="loading" class="qt-block-ui"></div>
  </div>
</template>

<script>
import Vue from 'vue';
import Pagination from '@/components/Pagination.vue';

export default {
  name: 'DataTable',
  components: {
    Pagination,
  },
  props: {
    clickable: Boolean,
    searchable: Boolean,
    loading: Boolean,
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    pages: Number,
  },
  data() {
    return {
      q: '',
    };
  },
  computed: {
    filteredColumns() {
      const filteredColumns = [];

      this.columns.forEach((column) => {
        if (!column.hide) filteredColumns.push(column);
      });
      return filteredColumns;
    },
  },
  methods: {
    getColumnLabel(value) {
      if (value.label) return value.label;
      return this.getColumnKey(value);
    },
    getColumnKey(value) {
      return typeof value === 'string' ? value : value.key;
    },
    getColumnValue(item, column) {
      const keys = this.getColumnKey(column).split('.');

      for (let i = 0; i < keys.length; i += 1) {
        item = item[keys[i]];
        if (Array.isArray(item)) {
          item = item[0] ? item[0] : '';
        }
      }
      if (item === null) return '-';
      if (item === true) {
        return '<i class="fas fa-check color-green"></i>';
      }
      if (item === false) {
        return '-';
      }

      if (column.filters) {
        column.filters.forEach((filter) => {
          const filterParams = filter.split('|');
          item = Vue.filter(filterParams[0])(item, filterParams[1]);
        });
      }
      if (column.localize) {
        const loc = this.$tc(`app.${item}`, 1);

        if (loc.indexOf('app.') === -1) item = loc;
      }
      return item;
    },
    getColumnClass(item, column) {
      return typeof column.class === 'object'
        ? column.class[item[column.key]]
        : column.class;
    },
    changePage(page) {
      this.$emit('change-page', page);
    },
    search() {
      this.$emit('search', this.q);
    },
  },
};
</script>

<style scoped lang="scss">
.clickable {
  cursor: pointer;
}

table {
  border-collapse: separate;
  border-spacing: 0 10px;

  &.busy {
    opacity: 0.55;
  }
}

thead tr {
  border-bottom: 3px solid rgba(107, 35, 220, 0.14);
  font-size: 16px;
  width: 100%;
}

td {
  vertical-align: middle;
  border-radius: 8px;
  width: 25%;
  text-align: start;
}

th {
  vertical-align: middle;
  border-radius: 8px;
  width: 30%;
  text-align: start;
}

tbody tr {
  padding: 0.5rem;
  width: 100%;
  border-spacing: 5px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-top: 1rem;
}

@media (max-width: 1200px) {
  .table-wrapper table tbody tr {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    text-align: center;
    border: 1px solid #E5E5E5;
    border-radius: 6px;
  }

  .table-wrapper table tbody tr td {
    text-align: end;
  }

  table {
    thead {
      display: none;
    }

    td {
      text-align: right;
      position: relative;
      padding-left: 50%;
      height: auto;
      border: none;
      width: 100% !important;
      box-sizing: border-box;

      &:before {
        content: attr(data-label);
        position: absolute;
        left: 16px;
        width: calc(50% - 16px);
        text-align: left;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
